import React from "react";
import { Box, Flex, Text, Icon, CloseButton, useToast } from "@chakra-ui/react";
import {
  HiOutlineCheckCircle,
  HiOutlineExclamationTriangle,
  HiOutlineInformationCircle,
  HiOutlineXCircle,
} from "react-icons/hi2";
import { PALETTES } from "./appColors";

// Toasts render in a portal on <body>, outside the .stande-app wrapper, so CSS
// variables don't resolve here - use literal palette values. Status accents use
// the brand system: success + info -> blue, warning -> amber, error -> red.
const STATUS = {
  success: { icon: HiOutlineCheckCircle, light: "#0B6E8C", dark: "#9AE9FE" },
  info: { icon: HiOutlineInformationCircle, light: "#0B6E8C", dark: "#9AE9FE" },
  warning: { icon: HiOutlineExclamationTriangle, light: "#9A6B0B", dark: "#F2C14E" },
  error: { icon: HiOutlineXCircle, light: "#C0392B", dark: "#FF8A7A" },
};

/**
 * Stande-app-styled toast. Same call signature as Chakra's useToast:
 *   const toast = useAppToast(isDark);
 *   toast({ title, description, status, duration });
 */
export function useAppToast(isDark = false) {
  const toast = useToast();
  const p = isDark ? PALETTES.dark : PALETTES.light;

  return ({ title, description, status = "info", duration = 3000, ...rest }) => {
    const meta = STATUS[status] || STATUS.info;
    const accent = isDark ? meta.dark : meta.light;

    return toast({
      duration,
      position: "bottom-right",
      ...rest,
      render: ({ onClose }) => (
        <Flex
          align="flex-start"
          gap="12px"
          padding="14px 16px"
          mb="12px"
          mr="4px"
          minWidth="280px"
          maxWidth="380px"
          borderRadius="12px"
          background={p.surface}
          border="1px solid"
          borderColor={p.border}
          boxShadow="0 16px 40px -16px rgba(0,0,0,0.45)"
          position="relative"
          overflow="hidden"
        >
          <Box position="absolute" left="0" top="0" bottom="0" width="3px" background={accent} />

          <Flex
            align="center"
            justify="center"
            boxSize="26px"
            borderRadius="8px"
            background={p.surfaceSubtle}
            mt="1px"
            flexShrink={0}
          >
            <Icon as={meta.icon} boxSize="16px" color={accent} />
          </Flex>

          <Box flex="1" minWidth="0">
            {title && (
              <Text fontSize="13.5px" fontWeight="600" color={p.text} lineHeight="1.35">
                {title}
              </Text>
            )}
            {description && (
              <Text fontSize="12.5px" color={p.textSub} mt="2px" lineHeight="1.45">
                {description}
              </Text>
            )}
          </Box>

          <CloseButton
            size="sm"
            onClick={onClose}
            color={p.textMuted}
            borderRadius="full"
            _hover={{ bg: p.hover }}
            flexShrink={0}
            mt="-2px"
            mr="-4px"
          />
        </Flex>
      ),
    });
  };
}
